/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Own high-frequency roster selection and guardian channel choice.
 * @state-owner module-scoped Zustand
 * @allowed-imports Zustand; shared view types; shared transient reset registry
 * @forbidden server entities; roles; permissions; tokens
 * @public-api classrooms module only
 * @side-effects in-memory UI updates
 * @tests modules/classrooms/model/next-lesson.test.ts and module-contracts.test.ts
 */

import { create } from 'zustand';
import type { GuardianChannel } from '../../../shared/lib/model';
import { registerWorkspaceTransientReset } from '../../../shared/lib/transient-reset';

interface ClassroomUiState {
  selectedStudents: readonly string[];
  guardianChannel: GuardianChannel;
  presentingFor: string | null;
  toggleStudent(name: string): void;
  clearStudents(): void;
  setGuardianChannel(channel: GuardianChannel): void;
  setPresentingFor(student: string | null): void;
  reset(): void;
}

export const useClassroomUiStore = create<ClassroomUiState>((set) => ({
  selectedStudents: [],
  guardianChannel: 'line',
  presentingFor: null,

  toggleStudent: (name) =>
    set((state) => ({
      selectedStudents: state.selectedStudents.includes(name)
        ? state.selectedStudents.filter((item) => item !== name)
        : [...state.selectedStudents, name],
    })),
  clearStudents: () => set({ selectedStudents: [] }),
  setGuardianChannel: (guardianChannel) => set({ guardianChannel }),
  setPresentingFor: (presentingFor) => set({ presentingFor }),
  reset: () => set({ selectedStudents: [], guardianChannel: 'line', presentingFor: null }),
}));

registerWorkspaceTransientReset('classrooms-ui', () => useClassroomUiStore.getState().reset());
